// @flow
import {
    AspectRatioCard,
    AspectRatioCardImage,
    AspectRatioCardTitle
} from '@homeaway/react-aspect-ratio-card';
import CardCarousel from '@homeaway/react-card-carousel';
import {debounce} from 'lodash';
import React, {Component} from 'react';
import {injectIntl} from 'react-intl';
import {connect} from 'react-redux';
import {compose} from 'redux';
import routerActions from '../../actions/router';
import keyboardConstants from '../../constants/keyboardConstants';
import {media} from '../../utils/breakpoints';
import {GENERIC_EVENT, trigger as edapTrigger} from '../../utils/edapWrapper';
import withLingua from '../withLingua/withLingua';
import './SearchTermCarousel.less';

import type {Node} from 'react';
import type {State as ReduxState} from '../../reducers/index';
import gql from 'graphql-tag';

import type {LinguaProps} from '../withLingua/withLingua';
import type {IntlShape} from 'react-intl';
import type {TypeaheadSuggestion} from '../../queries/searchQuery';
import type DiscoveryFeeds from '@homeaway/graphql-traveler-discovery';

const LINGUA_KEYS = {
    carousel_title: 'carousel.popularPlaceTitle',
    carousel_next: 'carousel.next',
    carousel_previous: 'carousel.previous'
};

const commonSelectionSet = `
    discoveryXploreFeeds {
        results {
            id
            title
            items {
                ... on SearchDiscoveryFeedItem {
                    type
                    imageHref
                    place {
                        uuid
                        name {
                            full
                            simple
                        }
                    }
                }
            }
        }
    }
    typeaheadSuggestion {
        name
    }
`;

export const fragments = {
    results: gql`
        fragment SearchTermCarouselResults on Results {
            ${commonSelectionSet}
        }
    `,
    searchResult: gql`
        fragment SearchTermCarouselSearchResult on SearchResult {
            ${commonSelectionSet}
        }
    `
};

type State = {
    index: number
};

type StateProps = {
    isXsScreenWidth: boolean,
    isBoundingBoxSearch?: boolean
};

type OwnProps = {
    aspectRatio: string,
    discoveryXploreFeeds: any,
    intl: IntlShape,
    locationSearch: typeof routerActions.search,
    typeaheadSuggestion: TypeaheadSuggestion
};

type Props = OwnProps & StateProps & LinguaProps;

type DiscoveryXploreFeedsItem = {
    type: string,
    imageHref?: string,
    place?: {
        uuid: string,
        name: {
            full: string,
            simple: string
        }
    }
};

export class SearchTermCarousel extends Component<Props, State> {
    static defaultProps = {
        aspectRatio: '4x3'
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            index: 0
        };
    }

    componentDidMount() {
        const carouselId = this.props.discoveryXploreFeeds.results[0].id;
        edapTrigger(GENERIC_EVENT, {
            eventaction: `searchTerm carousel presented ${carouselId}`,
            highfrequency: 'true'
        });
    }

    // handle onScroll event for mobile scrolling
    handleScroll = debounce(() => {
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'searchTerm carousel',
            highfrequency: 'true',
            eventlabel: 'advance'
        });
    }, 200);
    // Centers the controls within the image using the method from AspectRatioCard
    getControlOffset = (aspectRatio: string) => {
        return (width: number, controlHeight: string) => {
            return AspectRatioCard.getControlOffset(
                width,
                controlHeight,
                aspectRatio
            );
        };
    };

    handlePrevNext = (
        evt: SyntheticEvent<HTMLInputElement>,
        newIndex: number
    ) => {
        this.setState({index: newIndex});
        edapTrigger(GENERIC_EVENT, {
            eventaction: 'searchTerm carousel',
            highfrequency: 'true',
            eventlabel: 'advance'
        });
    };

    getDestinationName = (
        destination: DiscoveryXploreFeedsItem,
        type: string
    ) => {
        return (
            (destination.place &&
                destination.place.name &&
                destination.place.name[type]) ||
            ''
        );
    };

    getDestinationUuid = (destination: DiscoveryXploreFeedsItem) => {
        return (destination.place && destination.place.uuid) || '';
    };

    handleOnClick = (
        evt: SyntheticEvent<HTMLInputElement>,
        uuid: string,
        name: string,
        i: number
    ) => {
        let q = evt.currentTarget.getAttribute('value');
        if (q) {
            this.props.locationSearch({q});
        }

        const index = `card-${i + 1}`;
        const options = {delayProcessing: true};
        edapTrigger(
            GENERIC_EVENT,
            {
                eventaction: 'searchTerm carousel',
                eventlabel: 'click',
                logmessage: `destinationUUID: ${uuid}, destinationName: ${name}, cardIndex: ${index}`
            },
            options
        );
    };

    renderCards = (discoveryXploreFeeds: DiscoveryFeeds) => {
        return (
            // purposely selecting first element in array
            discoveryXploreFeeds.results[0].items.map<Node>(
                (destination, i) => {
                    const destinationFullName = this.getDestinationName(
                        destination,
                        'full'
                    );

                    const destinationSimpleName = this.getDestinationName(
                        destination,
                        'simple'
                    );

                    const destinationUuid = this.getDestinationUuid(
                        destination
                    );

                    const handleClickEvent = (e) => {
                        this.handleOnClick(
                            e,
                            destinationUuid,
                            destinationFullName,
                            i
                        );
                    };

                    const handleEnterEvent = (e) => {
                        this.handleIfEnterPressed(
                            e,
                            destinationUuid,
                            destinationFullName,
                            i
                        );
                    };

                    if (!destination.imageHref) {
                        edapTrigger(GENERIC_EVENT, {
                            eventaction: 'searchTerm carousel',
                            eventlabel: 'image',
                            logmessage: `destinationUuid: ${destinationUuid} is missing an image`
                        });
                    }

                    return (
                        <div
                            className="SearchTermCarousel__link"
                            onClick={handleClickEvent}
                            onKeyDown={handleEnterEvent}
                            value={destinationFullName}
                            role="button"
                            tabIndex="0"
                            key={`card-${i + 1}`}
                        >
                            <AspectRatioCard
                                aspectRatio={this.props.aspectRatio}
                            >
                                <AspectRatioCardImage
                                    data-slot={'figure'}
                                    imageUri={destination.imageHref}
                                />
                                <AspectRatioCardTitle
                                    data-slot={'caption'}
                                    title={destinationSimpleName}
                                />
                            </AspectRatioCard>
                        </div>
                    );
                }
            )
        );
    };

    handleIfEnterPressed = (
        evt: SyntheticEvent<HTMLInputElement>,
        uuid: string,
        name: string,
        i: number
    ) => {
        if (
            evt.nativeEvent instanceof KeyboardEvent &&
            evt.nativeEvent.keyCode === keyboardConstants.ENTER
        ) {
            this.handleOnClick(evt, uuid, name, i);
        }
    };

    render() {
        const {props} = this;
        const {formatMessage} = props.intl;
        const nextLabel = props.messages[LINGUA_KEYS.carousel_next];
        const previousLabel = props.messages[LINGUA_KEYS.carousel_previous];
        const title = props.messages[LINGUA_KEYS.carousel_title];
        const {typeaheadSuggestion, discoveryXploreFeeds} = this.props;

        if (this.props.isBoundingBoxSearch) {
            return <div />;
        }
        const popularPlaceTitle = formatMessage(
            {
                id: 'popularPlaceTitle',
                defaultMessage: title
            },
            {
                placeName: typeaheadSuggestion.name
            }
        );

        return !props.isXsScreenWidth ? (
            <div>
                <h3 className="SearchTermCarousel__title">
                    {popularPlaceTitle}
                </h3>
                <div className="SearchTermCarousel__container">
                    {this.renderCards({
                        results: [
                            {
                                items: discoveryXploreFeeds.results[0].items.slice(
                                    0,
                                    5
                                )
                            }
                        ]
                    })}
                </div>
            </div>
        ) : (
            <div onScroll={this.handleScroll}>
                <CardCarousel
                    breakpoints={{
                        '1200': 7,
                        '992': 7,
                        '768': 5,
                        '480': 4,
                        '0': 3
                    }}
                    cardIndex={this.state.index}
                    getControlOffset={this.getControlOffset(props.aspectRatio)}
                    onNextClick={this.handlePrevNext}
                    onPreviousClick={this.handlePrevNext}
                    nextLabel={nextLabel}
                    previousLabel={previousLabel}
                    title={popularPlaceTitle}
                    scrolling={props.isXsScreenWidth}
                >
                    {this.renderCards(discoveryXploreFeeds)}
                </CardCarousel>
            </div>
        );
    }
}

const mapStateToProps = (state: ReduxState): StateProps => {
    let {viewport, searchParams} = state;
    const breakpoint = viewport.breakpoint;
    const isXsScreenWidth = !media(breakpoint, {
        minWidth: 'SM_MIN'
    });

    const isBoundingBoxSearch = searchParams.isBoundingBoxSearch;

    return {
        isXsScreenWidth,
        isBoundingBoxSearch
    };
};

const mapDispatchToProps = {
    locationSearch: routerActions.locationSearch
};

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps),
    withLingua({
        namespace: 'discovery/xplorer',
        category: 'messages'
    })
)(SearchTermCarousel);
